import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Chip, 
  Divider, 
  List, 
  ListItem, 
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Link
} from '@mui/material';
import { 
  LocalShipping, 
  CheckCircle, 
  Warning, 
  Schedule, 
  Inventory, 
  Store, 
  Business,
  CalendarToday,
  AccessTime,
  Receipt,
  LocationOn,
  Info,
  OpenInNew
} from '@mui/icons-material';
// Import temperature icons separately
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { shipmentService } from '../../services/shipment.service';
import { orderService } from '../../services/order.service';
import CarrierTrackingDialog from './CarrierTrackingDialog';

// Helper function to check temperature alert status
const checkTemperatureAlert = (shipment) => {
  if (!shipment || shipment.temperature === undefined || !shipment.temperature_range) {
    return { hasAlert: false };
  }
  
  const temp = shipment.temperature;
  const range = shipment.temperature_range;
  
  // Check if temperature is outside the defined range
  const isTooWarm = temp > range.max;
  const tooCold = temp < range.min;
  
  if (isTooWarm || tooCold) {
    return {
      hasAlert: true,
      alertType: isTooWarm ? 'warm' : 'cold',
      message: isTooWarm 
        ? `Temperature too warm: ${temp}°C (max: ${range.max}°C)` 
        : `Temperature too cold: ${temp}°C (min: ${range.min}°C)`
    };
  }
  
  return { hasAlert: false };
};

const ShipmentDetails = ({ selectedShipmentId, shipments }) => {
  const [shipmentData, setShipmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDetailsLoading, setPoDetailsLoading] = useState(false);
  const [poDetailsError, setPoDetailsError] = useState(null);
  const [poDetails, setPoDetails] = useState(null);
  
  // Add state for telemetry toggle
  const [showTelemetry, setShowTelemetry] = useState(false);
  
  // Add state for temperature alert
  const [temperatureAlert, setTemperatureAlert] = useState({ hasAlert: false });

  // Add state for carrier tracking dialog
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState({
    carrierId: '',
    trackingNumber: '',
    carrierName: ''
  });

  // Add useEffect for telemetry toggle notification
  useEffect(() => {
    // Notify parent component about telemetry toggle state
    const event = new CustomEvent('telemetryToggle', { 
      detail: { showTelemetry } 
    });
    window.dispatchEvent(event);
  }, [showTelemetry]);

  // Toggle telemetry display
  const handleTelemetryToggle = () => {
    setShowTelemetry(!showTelemetry);
  };

  useEffect(() => {
    const fetchShipmentDetails = async () => {
      if (!selectedShipmentId) {
        setShipmentData(null);
        setTemperatureAlert({ hasAlert: false });
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log(`Fetching details for shipment: ${selectedShipmentId}`);
        
        // Using shipmentService to get shipment details
        const response = await shipmentService.getShipmentById(selectedShipmentId);
        
        // Log the full response to see what we're getting
        console.log("API Response:", JSON.stringify(response, null, 2));
        
        // Check if response is valid and has data property
        if (!response || !response.data) {
          throw new Error('Empty or invalid response received from API');
        }
        
        // Extract the actual shipment data from the response.data property
        const shipmentData = response.data;
        console.log("Extracted shipment data:", shipmentData);
        
        // Set shipment data
        setShipmentData(shipmentData);
        
        // Check for temperature alerts
        const tempAlert = checkTemperatureAlert(shipmentData);
        console.log("Temperature alert check:", tempAlert);
        setTemperatureAlert(tempAlert);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching shipment details:', err);
        
        // Fallback to using data from shipments list if API call fails
        const existingShipment = shipments.find(s => s.shipment_id === selectedShipmentId);
        if (existingShipment) {
          console.log("Using data from shipments list as fallback:", existingShipment);
          setShipmentData(existingShipment);
          
          // Check for temperature alerts in fallback data
          const tempAlert = checkTemperatureAlert(existingShipment);
          console.log("Temperature alert check (fallback):", tempAlert);
          setTemperatureAlert(tempAlert);
          
          setError(null);
        } else {
          setError('Failed to load shipment details');
        }
        setLoading(false);
      }
    };

    fetchShipmentDetails();
  }, [selectedShipmentId, shipments]);

  // Helper function to get status icon
  const getStatusIcon = (status) => {
    // Check if status exists and is a string
    if (!status || typeof status !== 'string') {
      return <Schedule color="action" />;
    }
    
    // Now safely convert to uppercase
    const upperStatus = status.toUpperCase();
    
    switch (upperStatus) {
      case 'DELIVERED':
        return <CheckCircle color="success" />;
      case 'IN_TRANSIT':
      case 'SHIPPED':
        return <LocalShipping color="primary" />;
      case 'DELAYED':
        return <Warning color="warning" />;
      case 'FAILED':
      case 'EXCEPTION':
        return <Warning color="error" />;
      default:
        return <Schedule color="action" />;
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to handle PO click and open dialog
  const handlePoClick = async (poNumber) => {
    setSelectedPO(poNumber);
    setPoDialogOpen(true);
    setPoDetailsLoading(true);
    setPoDetailsError(null);
    
    try {
      // Using orderService instead of direct axios call
      const response = await orderService.getPODetails(poNumber);
      
      if (response && response.status === 'success') {
        setPoDetails(response.data);
      } else {
        setPoDetailsError('Failed to load PO details');
      }
    } catch (err) {
      console.error('Error fetching PO details:', err);
      setPoDetailsError(`Error: ${err.message || 'Failed to load PO details'}`);
    } finally {
      setPoDetailsLoading(false);
    }
  };

  // Function to close the PO dialog
  const handleClosePoDialog = () => {
    setPoDialogOpen(false);
    setSelectedPO(null);
    setPoDetails(null);
  };

  // Function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Extract PO numbers from stops
  const getUniquePoNumbers = () => {
    if (!shipmentData || !shipmentData.stops || !Array.isArray(shipmentData.stops)) {
      return [];
    }
    
    const poNumbers = shipmentData.stops
      .filter(stop => stop.po_number)
      .map(stop => stop.po_number);
    
    return [...new Set(poNumbers)]; // Remove duplicates
  };

  // Helper function to render temperature alert chip
  const renderTemperatureAlert = () => {
    if (!temperatureAlert.hasAlert) return null;
    
    return (
      <Tooltip title={temperatureAlert.message}>
        <Chip
          icon={temperatureAlert.alertType === 'warm' ? <ThermostatIcon /> : <AcUnitIcon />}
          label={temperatureAlert.alertType === 'warm' ? "Too Warm" : "Too Cold"}
          color="error"
          variant="outlined"
          size="small"
          sx={{ ml: 1 }}
        />
      </Tooltip>
    );
  };

  // Function to handle tracking number click
  const handleTrackingClick = (carrierId, trackingNumber, carrierName) => {
    console.log("Tracking clicked:", { carrierId, trackingNumber, carrierName });
    setSelectedTracking({
      carrierId: carrierId || "UNKNOWN", // Provide a fallback
      trackingNumber,
      carrierName: carrierName || "Carrier"
    });
    setTrackingDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!shipmentData) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Select a shipment to view details
        </Typography>
      </Box>
    );
  }

  // Get unique PO numbers
  const poNumbers = getUniquePoNumbers();
  console.log("PO Numbers:", poNumbers);
  
  // Safely get status for display
  const displayStatus = shipmentData.status && typeof shipmentData.status === 'string' 
    ? shipmentData.status 
    : 'Unknown';

  // Safely determine status color
  let statusColor = 'default';
  if (shipmentData.status && typeof shipmentData.status === 'string') {
    const status = shipmentData.status.toUpperCase();
    if (status === 'DELIVERED') statusColor = 'success';
    else if (status === 'IN_TRANSIT' || status === 'SHIPPED') statusColor = 'primary';
    else if (status === 'DELAYED') statusColor = 'warning';
    else if (status === 'FAILED' || status === 'EXCEPTION') statusColor = 'error';
  }

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          Shipment Details
        </Typography>
        
        {/* Add telemetry toggle switch */}
        <FormControlLabel
          control={
            <Switch 
              checked={showTelemetry}
              onChange={handleTelemetryToggle}
              color="primary"
            />
          }
          label="Show Telemetry Data"
        />
      </Box>
      
      <Grid container spacing={2}>
        {/* Header */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" component="h2">
              Shipment {shipmentData.shipment_id || 'Unknown'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip 
                icon={getStatusIcon(shipmentData.status)} 
                label={displayStatus} 
                color={statusColor}
                variant="outlined"
              />
              {/* Render temperature alert chip if needed */}
              {renderTemperatureAlert()}
            </Box>
          </Box>
        </Grid>

        {/* Temperature Information */}
        {shipmentData.temperature !== undefined && shipmentData.temperature_range && (
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: temperatureAlert.hasAlert ? '1px solid #f44336' : '1px solid #e0e0e0',
              mb: 2
            }}>
              {temperatureAlert.alertType === 'warm' ? (
                <ThermostatIcon sx={{ mr: 1, color: temperatureAlert.hasAlert ? 'error.main' : 'success.main' }} />
              ) : temperatureAlert.alertType === 'cold' ? (
                <AcUnitIcon sx={{ mr: 1, color: temperatureAlert.hasAlert ? 'error.main' : 'success.main' }} />
              ) : (
                <ThermostatIcon sx={{ mr: 1, color: 'success.main' }} />
              )}
              <Box>
                <Typography variant="subtitle2">
                  Current Temperature: {shipmentData.temperature}°C
                </Typography>
                <Typography variant="body2">
                  Required Range: {shipmentData.temperature_range.min}°C to {shipmentData.temperature_range.max}°C
                  {temperatureAlert.hasAlert && (
                    <Typography variant="body2" color="error" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                      {temperatureAlert.message}
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Basic Shipment Information */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
              Origin
            </Typography>
            <Typography variant="body1">
              {shipmentData.origin?.name || 'Unknown Origin'}
            </Typography>
            {shipmentData.origin && (
              <Typography variant="body2" color="text.secondary">
                Lat: {shipmentData.origin.lat}, Lng: {shipmentData.origin.lng}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <Store sx={{ mr: 1, verticalAlign: 'middle' }} />
              Destination
            </Typography>
            <Typography variant="body1">
              {shipmentData.destination?.name || 'Unknown Destination'}
            </Typography>
            {shipmentData.destination && (
              <Typography variant="body2" color="text.secondary">
                Lat: {shipmentData.destination.lat}, Lng: {shipmentData.destination.lng}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
              Departure
            </Typography>
            <Typography variant="body1">
              {formatDate(shipmentData.departure_time)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
              Estimated Arrival
            </Typography>
            <Typography variant="body1">
              {formatDate(shipmentData.estimated_arrival)}
            </Typography>
          </Box>
        </Grid>

        {shipmentData.tracking_number && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                Tracking Number
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ mr: 1 }}>
                  {shipmentData.tracking_number}
                </Typography>
                {shipmentData.carrier && (
                  <Chip 
                    size="small" 
                    label={shipmentData.carrier} 
                    color="primary" 
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                )}
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => handleTrackingClick(
                    shipmentData.carrier,
                    shipmentData.tracking_number,
                    shipmentData.carrier_name || 'Carrier'
                  )}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        )}

        {shipmentData.vehicle_type && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
                Vehicle Type
              </Typography>
              <Typography variant="body1">
                {shipmentData.vehicle_type}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Items Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
            Items
          </Typography>
          
          {shipmentData.items && shipmentData.items.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipmentData.items.map((item, index) => (
                    <TableRow key={`${item.item_id}-${index}`}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.item_id}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No items in this shipment
            </Typography>
          )}
        </Grid>

        {/* Stops Section */}
        {shipmentData.stops && shipmentData.stops.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
              Stops
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sequence</TableCell>
                    <TableCell>Store</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>PO Number</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipmentData.stops.map((stop, index) => (
                    <TableRow key={`stop-${index}`}>
                      <TableCell>{stop.stop_sequence}</TableCell>
                      <TableCell>{stop.store_name}</TableCell>
                      <TableCell>
                        {stop.latitude && stop.longitude ? 
                          `${stop.latitude.toFixed(4)}, ${stop.longitude.toFixed(4)}` : 
                          'N/A'}
                      </TableCell>
                      <TableCell>
                        {stop.po_number ? (
                          <Link 
                            component="button"
                            variant="body2"
                            onClick={() => handlePoClick(stop.po_number)}
                          >
                            {stop.po_number}
                          </Link>
                        ) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>

      {/* PO Details Dialog */}
      <Dialog
        open={poDialogOpen}
        onClose={handleClosePoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Purchase Order Details: {selectedPO}
        </DialogTitle>
        <DialogContent dividers>
          {poDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : poDetailsError ? (
            <Alert severity="error">{poDetailsError}</Alert>
          ) : poDetails ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Order Date</Typography>
                  <Typography variant="body1">{formatDate(poDetails.order_date)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip 
                    label={poDetails.status} 
                    color={
                      poDetails.status === 'COMPLETED' ? 'success' :
                      poDetails.status === 'PROCESSING' ? 'primary' :
                      poDetails.status === 'PENDING' ? 'warning' : 'default'
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Supplier</Typography>
                  <Typography variant="body1">{poDetails.supplier_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Total Amount</Typography>
                  <Typography variant="body1">{formatCurrency(poDetails.total_amount)}</Typography>
                </Grid>
              </Grid>
              
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Items</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {poDetails.items && poDetails.items.map((item, index) => (
                      <TableRow key={`po-item-${index}`}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Typography>No details available for this purchase order.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePoDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Carrier Tracking Dialog */}
      <CarrierTrackingDialog
        open={trackingDialogOpen}
        onClose={() => setTrackingDialogOpen(false)}
        carrierId={selectedTracking.carrierId}
        trackingNumber={selectedTracking.trackingNumber}
        carrierName={selectedTracking.carrierName}
      />
    </Box>
  );
};

export default ShipmentDetails; 